import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BaseModalComponent } from '../../../../models/base/base-modal.component';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileToJsonService } from '../../../remittances/services/file-to-json.service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MembersFileRecord } from '../batch-add-members-file-upload/interfaces/members-file-record';
import { MembersTableColumnErrors } from './types/members-table-column-errors';
import { TypeService } from '../../../../services/type/type-service';
import { EmploymentType } from '../../../../models/employers/employer-history/types/employment-type';
import { EmploymentTypeEnum } from 'src/app/models/employers/enum/employment-type.enum';

@Component({
  selector: 'app-batch-add-members-file-preview-modal',
  templateUrl: './batch-add-members-file-preview-modal.component.html',
  styleUrls: ['./batch-add-members-file-preview-modal.component.scss'],
  providers: [FileToJsonService],
  changeDetection: ChangeDetectionStrategy.Default
})
export class BatchAddMembersFilePreviewModalComponent extends BaseModalComponent {
  public ignoreEscape: boolean = false;

  constructor(protected activeModal: NgbActiveModal) {
    super(activeModal);
  }

  private fileToJsonService = inject(FileToJsonService);
  private typeService = inject(TypeService);

  public provinces$ = this.typeService.provinces$;
  public employmentTypes$ = this.typeService.memberEmploymentTypes$;
  public typesAreLoaded$ = combineLatest([
    this.typeService.provincesLoaded$,
    this.typeService.memberEmploymentTypesLoaded$
  ]).pipe(map(([provincesLoaded, employmentTypesLoaded]) => provincesLoaded && employmentTypesLoaded));

  private _fileToPreview = new BehaviorSubject<File | null>(null);
  public fileToPreview$ = this._fileToPreview as Observable<File | null>;
  public setFileToPreview = (f: File) => this._fileToPreview.next(f);

  private _fileName = new BehaviorSubject<string>('');
  public fileName$ = this._fileName as Observable<string>;
  public setFileName = (name: string) => this._fileName.next(name);

  private _invalidRows = new BehaviorSubject<number[]>([]);
  public invalidRows$ = this._invalidRows as Observable<number[]>;
  public setInvalidRows = (rows: number[]) => this._invalidRows.next(rows);

  private _invalidColumns = new BehaviorSubject<MembersTableColumnErrors>({});
  public invalidColumns$ = this._invalidColumns as Observable<MembersTableColumnErrors>;
  public setInvalidColumns = (columns: MembersTableColumnErrors) => this._invalidColumns.next(columns);

  private _records = new BehaviorSubject<MembersFileRecord[]>([]);
  public records$ = this._records as Observable<MembersFileRecord[]>;

  public recordsToDisplay$ = combineLatest([this.records$, this.provinces$, this.employmentTypes$]).pipe(
    map(([records, provinces, employmentTypes]) => {
      return records.map(record => {
        const provinceCode = record['PROVINCE OF EMPLOYMENT']?.toString();
        const province = provinces.find(p => p.stateCode === provinceCode);
        const employmentType = this.getEmploymentType(record['EMPLOYMENT TYPE']?.toString(), employmentTypes);
        return {
          ...record,
          /* eslint-disable @typescript-eslint/naming-convention */
          'PROVINCE OF EMPLOYMENT': province,
          'EMPLOYMENT TYPE': employmentType
          /* eslint-enable @typescript-eslint/naming-convention */
        };
      });
    })
  );

  public hasErrors$ = this.invalidRows$.pipe(map(rows => rows.length > 0));

  public primaryLabel$ = this.hasErrors$.pipe(
    map(hasErrors => {
      return hasErrors ? $localize`Discard Upload` : $localize`Continue`;
    })
  );

  private listenToFile = this.fileToPreview$.subscribeWhileAlive({
    owner: this,
    next: file => {
      if (!file) return;
      this.fileToJsonService.convertMemberFileToJson(file).then(records => {
        this._records.next(records);
        this.setFileName(file.name);
      });
    }
  });

  private getEmploymentType(shortCode: string, types: EmploymentType[]): EmploymentType | undefined {
    if (shortCode.toLowerCase() === 'ft') {
      return types.find(t => t.id === EmploymentTypeEnum.FullTime);
    } else if (shortCode.toLowerCase() === 'pt') {
      return types.find(t => t.id === EmploymentTypeEnum.PartTime);
    }
  }

  public primaryClicked(): void {
    combineLatest([this.hasErrors$, this.recordsToDisplay$]).once(([hasErrors, records]) => {
      this.activeModal.close(hasErrors ? { discardUpload: true } : { records, continue: true });
    });
  }

  public closeClicked(): void {
    combineLatest([this.hasErrors$, this.recordsToDisplay$]).once(([hasErrors, records]) => {
      this.activeModal.close(hasErrors ? { closedWithErrors: true } : { records, continue: false });
    });
  }

  setupBindings(): void {}

  setupViews(): void {}
}
